import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, KeyIcon, UserIcon } from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, SimpleNotificationContainer } from '../../components/ui';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { authService } from '../../services/api';
import {
  applyApiErrorsToRhf,
  buildFormErrorBanner,
  formatApiErrorForUser,
  isApiRequestError,
} from '../../utils/apiErrors';

const changePasswordSchema = z.object({
  current_password: z.string()
    .min(1, 'La contraseña actual es requerida'),
  new_password: z.string()
    .min(6, 'La nueva contraseña debe tener al menos 6 caracteres'),
  confirm_password: z.string()
    .min(1, 'Confirma tu nueva contraseña'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Las contraseñas no coinciden",
  path: ["confirm_password"],
});

export const SettingsPage = () => {
  const { user } = useAuthStore();
  const { addNotification } = useUIStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordBanner, setPasswordBanner] = useState('');

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setPasswordBanner('');
    clearErrors();
    
    try {
      await authService.changePassword(data);
      addNotification({
        type: 'success',
        title: 'Contraseña cambiada',
        message: 'Tu contraseña ha sido actualizada exitosamente',
      });
      reset();
    } catch (error) {
      console.error('Change password error:', error);
      const msg = isApiRequestError(error)
        ? formatApiErrorForUser(error)
        : error?.message || 'Ha ocurrido un error inesperado';
      if (isApiRequestError(error)) {
        applyApiErrorsToRhf(error.body?.errors, setError, [
          'current_password',
          'new_password',
          'confirm_password',
        ]);
        setPasswordBanner(buildFormErrorBanner(error));
      } else {
        setPasswordBanner(msg);
      }
      addNotification({
        type: 'error',
        title: 'Error al cambiar contraseña',
        message: msg,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
        <p className="mt-1 text-sm text-gray-600">
          Gestiona tu perfil y configuración de cuenta
        </p>
      </div>

      {/* Información del Usuario */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserIcon className="h-5 w-5 mr-2" />
            Información del Usuario
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de Usuario
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-md">
                {user?.username}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-md">
                {user?.email}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre Completo
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-md">
                {user?.names} {user?.last_name}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-md">
                {user?.is_superuser ? 'Superusuario' : user?.is_staff ? 'Staff' : 'Usuario'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cambio de Contraseña */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <KeyIcon className="h-5 w-5 mr-2" />
            Cambiar Contraseña
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {passwordBanner && (
              <div
                role="alert"
                className="rounded-md bg-red-50 border border-red-200 p-3 text-red-800 text-sm whitespace-pre-wrap"
              >
                {passwordBanner}
              </div>
            )}
            <Input
              label="Contraseña Actual"
              type={showCurrentPassword ? "text" : "password"}
              placeholder="Ingresa tu contraseña actual"
              error={errors.current_password?.message}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title={showCurrentPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showCurrentPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              }
              {...register('current_password')}
            />

            <Input
              label="Nueva Contraseña"
              type={showNewPassword ? "text" : "password"}
              placeholder="Ingresa tu nueva contraseña"
              error={errors.new_password?.message}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              }
              {...register('new_password')}
            />

            <Input
              label="Confirmar Nueva Contraseña"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirma tu nueva contraseña"
              error={errors.confirm_password?.message}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              }
              {...register('confirm_password')}
            />

            <Button
              type="submit"
              className="w-full md:w-auto"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Cambiando contraseña...' : 'Cambiar Contraseña'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Global Notifications */}
      <SimpleNotificationContainer />
    </div>
  );
};
